import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * <inside-cure-picker>
 *
 * Selecteur de duree de cure (1 mois / 3 mois).
 *
 * Implemente en element personnalise et non en <script> inline : la fenetre
 * d'ajout rapide construit son contenu avec DOMParser puis cloneNode, et les
 * scripts issus de DOMParser ne s'executent jamais. Un element personnalise,
 * lui, est mis a niveau par le navigateur des son insertion dans le document.
 * Voir assets/quick-add.js (fetchProductPage).
 */
class InsideCurePicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-cure-form]');
    if (!this.form) return;

    this.button = this.querySelector('[data-cure-submit]');
    this.buttonText = this.querySelector('[data-cure-submit-text]');
    this.buttonPrice = this.querySelector('[data-cure-submit-price]');
    this.message = this.querySelector('[data-cure-message]');

    this.label = this.dataset.cta || 'Ajouter au panier';
    this.labelLoading = this.dataset.ctaLoading || 'Ajout en cours';
    this.labelSuccess = this.dataset.ctaSuccess || 'Ajoute';
    this.productUrl = this.dataset.productUrl || '';

    this.lastVariantId = null;
    this.galleryAbort = null;
    this.galleryCache = new Map();

    this.inputs = Array.from(this.querySelectorAll('input[name="id"]'));

    // Il suffit qu'UNE variante ait son propre visuel pour que le changement
    // ait du sens : en revenant sur une variante sans image dediee, on doit
    // retrouver le visuel par defaut du produit.
    this.hasVariantMedia = this.inputs.some((input) => Boolean(input.dataset.mediaId));

    this.inputs.forEach((input) => {
      if (input.checked) this.lastVariantId = input.value;
      input.addEventListener('change', () => this.handleChange(input));
    });

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.syncInitialSelection();
    this.prefetchGalleries();
  }

  /**
   * Le picker coche « 3 mois » cote serveur, mais la galerie et la ligne de
   * format sont rendues a partir de selected_or_first_available_variant, soit
   * « 1 mois » tant que l'URL ne porte pas de ?variant=. Sans ce rattrapage au
   * chargement, la page affiche la photo et le format de la mauvaise duree.
   */
  syncInitialSelection() {
    const selected = this.inputs.find((input) => input.checked);
    if (!selected) return;

    if (this.buttonPrice && selected.dataset.price) {
      this.buttonPrice.textContent = selected.dataset.price;
    }

    // Lu AVANT syncUrl : cette methode reecrit l'URL, et la relire ensuite
    // ferait croire que le serveur avait deja rendu la bonne galerie.
    const serverRendered = this.renderedVariantId();

    this.syncFormat(selected);
    this.syncUrl(selected.value);

    // Si l'URL demandait deja cette variante, le serveur a rendu la bonne
    // galerie : inutile de la retelecharger.
    if (serverRendered === selected.value) return;

    this.lastVariantId = null;
    this.swapGallery(selected.value);
  }

  /** La variante effectivement rendue par le serveur, d'apres l'URL. */
  renderedVariantId() {
    if (this.closest('.quick-add-modal')) return null;
    try {
      return new URL(window.location.href).searchParams.get('variant');
    } catch (error) {
      return null;
    }
  }

  handleChange(input) {
    if (this.buttonPrice) this.buttonPrice.textContent = input.dataset.price || '';
    this.syncFormat(input);
    this.syncUrl(input.value);
    this.swapGallery(input.value);
  }

  /**
   * La ligne de format sous le titre est rendue par le serveur et resterait
   * figee sur la variante initiale. On la tient a jour avec l'option choisie.
   */
  syncFormat(input) {
    const title = input.dataset.variantTitle;
    if (!title) return;

    const scope = this.closest('.quick-add-modal') || document;
    const target = scope.querySelector('.aime-format');
    if (target) target.textContent = title;
  }

  /** Reflete la variante dans l URL, comme le selecteur natif. */
  syncUrl(variantId) {
    if (this.closest('.quick-add-modal')) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    } catch (error) {
      /* URL non modifiable : sans consequence */
    }
  }

  /**
   * L'identifiant de la section qui contient la galerie. Permet de ne
   * demander que cette section au lieu de la page entiere.
   */
  get sectionId() {
    const section = this.closest('.shopify-section');
    return section?.id?.replace('shopify-section-', '') || '';
  }

  /** URL de rendu de la seule section galerie pour une variante donnee. */
  galleryUrl(variantId) {
    const section = this.sectionId;
    return `${this.productUrl}?variant=${variantId}${section ? `&section_id=${section}` : ''}`;
  }

  /**
   * Recupere le HTML de la galerie d'une variante, depuis le cache si
   * possible. Le resultat est mis en cache : un aller-retour entre deux
   * options ne declenche qu'une seule requete par variante.
   */
  fetchGallery(variantId, signal) {
    const cached = this.galleryCache.get(variantId);
    if (cached) return Promise.resolve(cached);

    return fetch(this.galleryUrl(variantId), { signal })
      .then((response) => response.text())
      .then((text) => {
        const found = new DOMParser().parseFromString(text, 'text/html').querySelector('media-gallery');
        const html = found?.outerHTML || null;
        if (html) this.galleryCache.set(variantId, html);
        return html;
      });
  }

  /** Telecharge a l'avance les galeries non affichees, sans bloquer. */
  prefetchGalleries() {
    if (!this.productUrl || !this.hasVariantMedia) return;

    const run = () =>
      this.inputs
        .filter((input) => input.value !== this.lastVariantId)
        .forEach((input) => this.fetchGallery(input.value).catch(() => {}));

    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 400);
  }

  /**
   * Remplace la galerie par celle de la variante choisie, en reprenant
   * l'approche du theme (assets/media-gallery.js : replaceWith).
   */
  swapGallery(variantId) {
    if (!this.productUrl || !this.hasVariantMedia) return;
    if (variantId === this.lastVariantId) return;

    const current = this.getGallery();
    if (!current) return;

    this.lastVariantId = variantId;
    this.galleryAbort?.abort();
    this.galleryAbort = new AbortController();

    this.fetchGallery(variantId, this.galleryAbort.signal)
      .then((html) => {
        if (!html) return;
        const live = this.getGallery();
        if (!live) return;
        const holder = document.createElement('div');
        holder.innerHTML = html;
        const fresh = holder.firstElementChild;
        if (fresh) live.replaceWith(fresh);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[inside-cure-picker] galerie :', error);
      });
  }

  /** La galerie de la modale si on y est, sinon celle de la page. */
  getGallery() {
    const modal = this.closest('.quick-add-modal');
    return (modal || document).querySelector('media-gallery');
  }

  handleSubmit(event) {
    event.preventDefault();
    if (!this.button || this.button.disabled) return;

    const selected = this.form.querySelector('input[name="id"]:checked') || this.inputs[0];
    if (!selected) return;

    this.button.disabled = true;
    this.setLabel(this.labelLoading);
    if (this.message) {
      this.message.textContent = '';
      this.message.classList.remove('is-error');
    }

    // Le tiroir et le compteur de panier ecoutent CartLinesUpdateEvent.
    // Un CustomEvent maison ne declenche rien : on reprend la sequence
    // du theme (voir assets/product-form.js).
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: selected.value, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(`${this.form.getAttribute('action')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: parseInt(selected.value, 10), quantity: 1 }] }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return Promise.reject(response);
        return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      })
      .then((response) => response.json())
      .then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'inside-cure-picker',
            sourceId: this.id || 'inside-cure-picker',
            itemCount: 1,
          },
        });
        this.button.disabled = false;
        this.setLabel(this.labelSuccess);
        setTimeout(() => this.setLabel(this.label), 1800);
      })
      .catch((error) => {
        deferred.reject?.(error);
        this.button.disabled = false;
        this.setLabel(this.label);
        if (this.message) {
          this.message.classList.add('is-error');
          this.message.textContent = error?.description || error?.message || 'Une erreur est survenue. Merci de réessayer.';
        }
      });
  }

  setLabel(text) {
    if (this.buttonText) this.buttonText.textContent = text;
  }
}

if (!customElements.get('inside-cure-picker')) {
  customElements.define('inside-cure-picker', InsideCurePicker);
}

/**
 * Lien « Ajouter » des vignettes du catalogue.
 *
 * Il ne doit pas ajouter la variante par defaut en silence : sur un produit a
 * plusieurs durees de cure, la cliente doit choisir. On delegue donc au bouton
 * natif du theme, qui ouvre la fenetre d'achat rapide (voir snippets/quick-add.liquid,
 * bouton .quick-add__button--choose).
 *
 * L'ecouteur est pose une seule fois sur le document : il couvre aussi les
 * vignettes chargees plus tard par le filtrage ou le defilement infini.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-aime-add]');
  if (!trigger) return;

  event.preventDefault();

  const card = trigger.closest('product-card');
  if (!card) return;

  // « Choisir » quand il y a plusieurs variantes, « Ajouter » sinon.
  const native =
    card.querySelector('.quick-add__button--choose') || card.querySelector('.quick-add__button--add');

  if (native) {
    native.click();
    return;
  }

  // Ajout rapide desactive dans les reglages : on ouvre la fiche produit.
  const link = card.querySelector('.product-card__link');
  if (link?.href) window.location.href = link.href;
});
